import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type AppRoleName =
  | 'system_admin'
  | 'manager'
  | 'rep'
  | 'capturer'
  | 'supplier';

export const ROLE_LABELS: Record<AppRoleName, string> = {
  system_admin: 'System Admin',
  manager: 'Manager',
  rep: 'Field Rep',
  capturer: 'Data Capturer',
  supplier: 'Supplier',
};

export function primaryRoleHome(roles: AppRoleName[], fallback = '/dashboard') {
  if (roles.includes('system_admin')) return '/admin';
  if (roles.includes('manager')) return '/staff/manager';
  if (roles.includes('rep')) return '/staff/rep';
  if (roles.includes('capturer')) return '/staff/capturer';
  if (roles.includes('supplier')) return '/supplier';
  return fallback;
}

export function useUserRoles() {
  const { session, loading: authLoading } = useAuth();
  const [roles, setRoles] = useState<AppRoleName[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (authLoading) return;
    if (!session?.user) {
      setRoles([]);
      setLoading(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id);
      if (!active) return;
      setRoles(((data ?? []) as Array<{ role: AppRoleName }>).map((r) => r.role));
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [session?.user?.id, authLoading]);

  const has = (r: AppRoleName) => roles.includes(r);
  const hasAny = (...r: AppRoleName[]) => r.some((x) => roles.includes(x));
  return { roles, loading, has, hasAny };
}
